import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { RouterModule } from '@angular/router';
import { DealsService } from '../../../core/services/deals.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';

interface Role {
  id: number;
  name: string;
  description?: string;
  selected: boolean;
}

interface Notification {
  key: string;
  name: string;
  description: string;
  requiredRoles: string[];
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  willReceive: boolean;
  preferenceEnabled: boolean;
}

@Component({
  selector: 'app-notification-test',
  templateUrl: './notification-test.component.html',
  styleUrl: './notification-test.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    FormsModule,
    RouterModule,
  ]
})
export class NotificationTestComponent implements OnInit {
  serverUrl = environment.serverUrl;

  // Rôles
  allRoles: Role[] = [];
  selectedRoles: string[] = [];

  // Notifications
  notifications: Notification[] = [];
  selectedNotification: Notification | null = null;

  // Utilisateurs qui recevront la notification
  targetUsers: User[] = [];

  // Chargement
  loading: boolean = false;
  loadingRoles: boolean = false;
  loadingNotifications: boolean = false;
  sending: boolean = false;

  constructor(
    private dealservice: DealsService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadRoles();
    this.loadNotifications();
  }

  loadRoles(): void {
    this.loadingRoles = true;
    this.dealservice.get_groups().subscribe({
      next: (response: any) => {

        if (Array.isArray(response)) {
          this.allRoles = response.map((role: any) => ({
            id: role.id,
            name: role.name,
            description: role.description,
            selected: false
          }));

        } else {
          this.allRoles = [];
        }
        this.loadingRoles = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loadingRoles = false;
        this.allRoles = [];
        this.cdr.detectChanges();
        this.snackBar.open('Erreur lors du chargement des rôles: ' + (error.error?.message || error.message || 'Erreur inconnue'), 'Fermer', {
          duration: 5000
        });
      }
    });
  }

  loadNotifications(): void {
    this.loadingNotifications = true;
    this.dealservice.get_all_notifications().subscribe({
      next: (response: any) => {

        if (response && response.status === 'success' && response.data) {
          this.notifications = response.data.map((notif: any) => ({
            key: notif.key || notif.name,
            name: notif.name,
            description: notif.description,
            requiredRoles: notif.required_roles || []
          }));

        } else {
          this.notifications = [];
        }
        this.loadingNotifications = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loadingNotifications = false;
        this.notifications = [];
        this.cdr.detectChanges();
        this.snackBar.open('Erreur lors du chargement des notifications: ' + (error.error?.message || error.message || 'Erreur inconnue'), 'Fermer', {
          duration: 5000
        });
      }
    });
  }

  toggleAllRoles(): void {
    const allSelected = this.allRoles.every(role => role.selected);
    this.allRoles.forEach(role => {
      role.selected = !allSelected;
    });
    this.updateSelectedRoles();
  }

  toggleRole(role: Role): void {
    role.selected = !role.selected;
    this.updateSelectedRoles();
  }

  updateSelectedRoles(): void {

    this.selectedRoles = this.allRoles
      .filter(role => role.selected)
      .map(role => role.name);

    if (this.selectedNotification && this.selectedRoles.length > 0) {
      this.calculateTargetUsers();
    } else if (this.selectedNotification && this.selectedRoles.length === 0) {
      this.targetUsers = [];
    }
  }

  onNotificationSelected(): void {
    if (this.selectedNotification) {
      // Si des rôles sont déjà sélectionnés, calculer immédiatement
      // Sinon, attendre que l'utilisateur sélectionne des rôles
      if (this.selectedRoles.length > 0) {
        this.calculateTargetUsers();
      } else {
        // Afficher un message pour indiquer qu'il faut sélectionner des rôles
        this.targetUsers = [];
      }
    } else {
      this.targetUsers = [];
    }
  }

  calculateTargetUsers(): void {
    if (!this.selectedNotification) {
      this.targetUsers = [];
      return;
    }

    this.loading = true;

    // Récupérer tous les utilisateurs avec leurs rôles
    this.dealservice.get_users_with_role().subscribe({
      next: (users: any[]) => {
        const filteredUsers: User[] = [];

        users.forEach((user: any) => {
          // Récupérer les rôles de l'utilisateur - le backend retourne un tableau d'objets avec role_name
          let userRoles: string[] = [];
          if (Array.isArray(user.roles)) {
            // Si c'est un tableau d'objets avec role_name
            userRoles = user.roles.map((r: any) => {
              if (typeof r === 'string') {
                return r;
              } else if (r.role_name) {
                return r.role_name;
              } else if (r.name) {
                return r.name;
              }
              return String(r);
            });
          } else if (user.groups && Array.isArray(user.groups)) {
            userRoles = user.groups.map((g: any) => g.name || g.role_name || g);
          } else if (user.group && Array.isArray(user.group)) {
            userRoles = user.group.map((g: any) => typeof g === 'string' ? g : (g.name || g.role_name || g));
          }

          console.log(`👤 Utilisateur ${user.first_name} ${user.last_name} (${user.email})`);
          console.log(`   Rôles bruts:`, user.roles);
          console.log(`   Rôles extraits:`, userRoles);
          console.log(`   Rôles sélectionnés:`, this.selectedRoles);

          // Si des rôles sont sélectionnés, vérifier que l'utilisateur a au moins un de ces rôles
          let hasSelectedRole = true;
          if (this.selectedRoles.length > 0) {
            hasSelectedRole = this.selectedRoles.some(selectedRole =>
              userRoles.some(userRole => userRole.toLowerCase() === selectedRole.toLowerCase())
            );
            console.log(`   ✓ A un rôle sélectionné: ${hasSelectedRole}`);
          }

          if (hasSelectedRole) {
            // Vérifier si l'utilisateur a les rôles requis pour la notification
            const notificationRequiredRoles = this.selectedNotification!.requiredRoles || [];
            const hasRequiredRole = notificationRequiredRoles.length === 0 ||
              notificationRequiredRoles.some(requiredRole =>
                userRoles.some(userRole => userRole.toLowerCase() === requiredRole.toLowerCase())
              );


            if (hasRequiredRole) {
              filteredUsers.push({
                id: user.id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                roles: userRoles,
                willReceive: false, // Sera mis à jour après vérification des préférences
                preferenceEnabled: false
              });
            }
          }
        });


        // Charger les préférences pour chaque utilisateur
        this.loadUserPreferences(filteredUsers);
      },
      error: (error: any) => {
        console.error('❌ Erreur lors du chargement des utilisateurs:', error);
        this.loading = false;
        this.snackBar.open('Erreur lors du chargement des utilisateurs: ' + (error.error?.message || error.message || 'Erreur inconnue'), 'Fermer', {
          duration: 5000
        });
      }
    });
  }

  loadUserPreferences(users: User[]): void {
    if (users.length === 0) {
      this.targetUsers = [];
      this.loading = false;
      return;
    }

    let loadedCount = 0;
    const totalUsers = users.length;

    users.forEach((user, index) => {
      this.http.get<any>(this.serverUrl + 'local/get_user_email_preferences/' + user.id).subscribe({
        next: (response: any) => {
          if (response.status === 'success' && response.data) {
            const pref = response.data.find((p: any) =>
              p.notification_key === this.selectedNotification!.key
            );
            user.preferenceEnabled = pref ? pref.enabled : false;
            user.willReceive = user.preferenceEnabled;
          }

          loadedCount++;
          if (loadedCount === totalUsers) {
            this.targetUsers = users;
            this.loading = false;
          }
        },
        error: (error: any) => {
          console.error('Erreur lors du chargement des préférences pour user ' + user.id, error);
          user.preferenceEnabled = false;
          user.willReceive = false;

          loadedCount++;
          if (loadedCount === totalUsers) {
            this.targetUsers = users;
            this.loading = false;
          }
        }
      });
    });
  }

  toggleUserPreference(user: User): void {
    if (!this.selectedNotification) return;

    this.loading = true;
    const newValue = !user.preferenceEnabled;

    const data = {
      user_id: user.id,
      preferences: {
        [this.selectedNotification.key]: newValue
      }
    };

    this.http.post<any>(this.serverUrl + 'local/save_user_email_preferences', data).subscribe({
      next: (response: any) => {
        if (response.status === 'success') {
          user.preferenceEnabled = newValue;
          user.willReceive = newValue;
          this.snackBar.open(
            `Préférence ${newValue ? 'activée' : 'désactivée'} pour ${user.first_name} ${user.last_name}`,
            'Fermer',
            { duration: 2000 }
          );
        }
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Erreur lors de la sauvegarde:', error);
        this.loading = false;
        this.snackBar.open('Erreur lors de la sauvegarde', 'Fermer', {
          duration: 3000
        });
      }
    });
  }

  sendNotification(): void {
    if (!this.selectedNotification || this.selectedRoles.length === 0) {
      this.snackBar.open('Veuillez sélectionner des rôles et une notification', 'Fermer', {
        duration: 3000
      });
      return;
    }

    if (this.targetUsers.length === 0) {
      this.snackBar.open('Aucun utilisateur ne recevra cette notification', 'Fermer', {
        duration: 3000
      });
      return;
    }

    this.sending = true;
    const usersToSend = this.targetUsers.filter(u => u.willReceive);

    if (usersToSend.length === 0) {
      this.snackBar.open('Aucun utilisateur n\'a activé cette notification', 'Fermer', {
        duration: 3000
      });
      this.sending = false;
      return;
    }

    const data = {
      notification_key: this.selectedNotification.key,
      role_names: this.selectedRoles,
      user_ids: usersToSend.map(u => u.id)
    };

    this.http.post<any>(this.serverUrl + 'local/send_notification_to_roles', data).subscribe({
      next: (response: any) => {
        this.sending = false;
        if (response.status === 'success') {
          this.snackBar.open(
            `Notification envoyée à ${response.sent_count || usersToSend.length} utilisateur(s)`,
            'Fermer',
            { duration: 5000 }
          );
        } else {
          let errorMsg = response.message || 'Erreur inconnue';
          if (response.errors && response.errors.length > 0) {
            errorMsg += '\n' + response.errors.join('\n');
          }
          this.snackBar.open('Erreur lors de l\'envoi: ' + errorMsg, 'Fermer', {
            duration: 8000,
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (error: any) => {
        this.sending = false;
        console.error('Erreur lors de l\'envoi:', error);
        const errorMessage = error.error?.message || error.message || 'Erreur inconnue';
        this.snackBar.open('Erreur lors de l\'envoi de la notification: ' + errorMessage, 'Fermer', {
          duration: 7000
        });
      }
    });
  }

  // Getters pour le template
  get allRolesSelected(): boolean {
    return this.allRoles.length > 0 && this.allRoles.every(r => r.selected);
  }

  get toggleAllButtonText(): string {
    return this.allRolesSelected ? 'Tout décocher' : 'Tout cocher';
  }

  get toggleAllButtonIcon(): string {
    return this.allRolesSelected ? 'check_box' : 'check_box_outline_blank';
  }

  get usersWillReceiveCount(): number {
    return this.targetUsers.filter(u => u.willReceive).length;
  }
}

